import React, {useEffect, useState} from "react";
import "./QuickPublishPopup.scss"
import Popup from "../../components/Popup/Popup";
import {
    selectIsPlaylistQuickPublishPopupOpen,
    selectIsTemplateQuickPublishPopupOpen,
    updateCreativesState
} from "../../state/creativesSlice";
import {useAddNewPlaylistTemplateMultipleMutation, useAddNewScreenPlaylistMultipleMutation, useGetCreationLabelsQuery, useGetPlaylistsQuery, useGetScreensQuery, useGetTemplatesQuery} from '../../services/api'
import ScheduleSettings from "./ScheduleSettings/ScheduleSettings";
import {useDispatch} from 'react-redux'
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux'
import CreationOrder from "./CreationOrder/CreationOrder";
import CreationSelector from "./CreationSelector/CreationSelector";

export function QuickPublishPopup() {
    const { t } = useTranslation();
    const dispatch = useDispatch()

    /* ---- API data ---- */
    // We request this data here, so the data fetching starts on page load and should be available for the popup instantly.
    const labelsQueryResult = useGetCreationLabelsQuery({pollingInterval: 3600000});
    const templatesQueryResult = useGetTemplatesQuery({pollingInterval: 3600000});
    const playlistsQueryResult = useGetPlaylistsQuery({pollingInterval: 3600000});
    const screensQueryResult = useGetScreensQuery({pollingInterval: 3600000});

    /* ---- Open template quick publish ---- */
    const isTemplateQuickPublishPopupOpen = useSelector(selectIsTemplateQuickPublishPopupOpen)
    const openTemplateQuickPublishPopup = ({templateId}) => {
        dispatch(updateCreativesState({isTemplateQuickPublishPopupOpen: true}));

        if (templatesQueryResult?.data) {
            setSelectedTemplateIds([templateId]);
            setSelectedAspectRatio(templatesQueryResult.data.find(template => template.id === templateId).aspectRatio);
        } else {
            setSelectedTemplateIds([]);
            setSelectedAspectRatio(null);
        }

        setSelectedPlaylistIds([]);

        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }
    useEffect(() => {
        window.openTemplateQuickPublishPopup = openTemplateQuickPublishPopup;
    }, [])


    /* ---- Open playlist quick publish ---- */
    const isPlaylistQuickPublishPopupOpen = useSelector(selectIsPlaylistQuickPublishPopupOpen)
    const openPlaylistQuickPublishPopup = ({playlistId}) => {
        dispatch(updateCreativesState({isPlaylistQuickPublishPopupOpen: true}));

        if (playlistsQueryResult?.data) {
            setSelectedPlaylistIds([playlistId]);
            setSelectedAspectRatio(playlistsQueryResult.data.find(playlist => playlist.id === playlistId).aspectRatio);
        } else {
            setSelectedPlaylistIds([]);
            setSelectedAspectRatio(null);
        }

        setSelectedScreenIds([]);

        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }
    useEffect(() => {
        window.openPlaylistQuickPublishPopup = openPlaylistQuickPublishPopup;
    }, [])

    /* ---- Close quick publish ---- */
    const closeQuickPublish = () => {
        if (isTemplateQuickPublishPopupOpen) {
            dispatch(updateCreativesState({isTemplateQuickPublishPopupOpen: false}))
        }
        if (isPlaylistQuickPublishPopupOpen) {
            dispatch(updateCreativesState({isPlaylistQuickPublishPopupOpen: false}))
        }
    };

    const [selectedAspectRatio, setSelectedAspectRatio] = useState(null);

    /* ---- Selection handling ---- */
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [selectedPlaylistIds, setSelectedPlaylistIds] = useState([]);
    const [selectedScreenIds, setSelectedScreenIds] = useState([]);

    const handleSelectedCreationsChange = (e, creationType, creationId) => {
        let newSelectedTemplateIds = selectedTemplateIds;
        let newSelectedPlaylistIds = selectedPlaylistIds;
        let newSelectedScreenIds = selectedScreenIds;

        // We don't set state immediately, we store it first, so we can use the new data within this function.

        if (e.target.checked) {
            // Creation gets selected. Add creation id to selected id's array
            if (creationType === 'template') {
                setTemplateSelectionError(false);
                newSelectedTemplateIds = [...selectedTemplateIds, creationId];
            }
            if (creationType === 'playlist') {
                setPlaylistSelectionError(false);
                newSelectedPlaylistIds = [...selectedPlaylistIds, creationId];
            }
            if (creationType === 'screen') {
                setScreenSelectionError(false);
                newSelectedScreenIds = [...selectedScreenIds, creationId];
            }
        } else if (!e.target.checked) {
            // Creation gets unselected, remove creationId from selected id's array
            if (creationType === 'template') {
                newSelectedTemplateIds = selectedTemplateIds.filter(currentTemplateId => currentTemplateId !== creationId);
            }
            if (creationType === 'playlist') {
                newSelectedPlaylistIds = selectedPlaylistIds.filter(currentPlaylistId => currentPlaylistId !== creationId);
            }
            if (creationType === 'screen') {
                newSelectedScreenIds = selectedScreenIds.filter(currentScreenId => currentScreenId !== creationId);
            }
        }

        // We collect all aspect ratios from selected creations
        const aspectRatios = [];

        newSelectedTemplateIds.forEach(creationId => {
            const creationRef = templatesQueryResult.data.find(creationQueryResult => creationQueryResult.id === creationId);
            if (creationRef) aspectRatios.push(creationRef.aspectRatio);
        });
        newSelectedPlaylistIds.forEach(creationId => {
            const creationRef = playlistsQueryResult.data.find(creationQueryResult => creationQueryResult.id === creationId);
            if (creationRef) aspectRatios.push(creationRef.aspectRatio);
        });
        newSelectedScreenIds.forEach(creationId => {
            const creationRef = screensQueryResult.data.find(creationQueryResult => creationQueryResult.id === creationId);
            if (creationRef) aspectRatios.push(creationRef.aspectRatio);
        });

        // We save only 1 aspect ratio, so only compatible creations can get selected afterwards
        const newAspectRatio = aspectRatios.find(aspectRatio => typeof aspectRatio === 'string');
        setSelectedAspectRatio(newAspectRatio)

        // Save the updated selected creations
        if (creationType === 'template') setSelectedTemplateIds(newSelectedTemplateIds);
        if (creationType === 'playlist') setSelectedPlaylistIds(newSelectedPlaylistIds);
        if (creationType === 'screen') setSelectedScreenIds(newSelectedScreenIds);
    }

    const [templateSelectionError, setTemplateSelectionError] = useState(false);
    const [playlistSelectionError, setPlaylistSelectionError] = useState(false);
    const [screenSelectionError, setScreenSelectionError] = useState(false);

    const [addNewPlaylistTemplateMultiple] = useAddNewPlaylistTemplateMultipleMutation()
    const [addNewScreenPlaylistMultiple] = useAddNewScreenPlaylistMultipleMutation()

    /* ---- Publish ---- */
    const onPublish = async (values) => {
        // Error handling
        setTemplateSelectionError(selectedTemplateIds.length === 0);
        setPlaylistSelectionError(selectedPlaylistIds.length === 0);
        setScreenSelectionError(selectedScreenIds.length === 0);

        if (isTemplateQuickPublishPopupOpen && (selectedTemplateIds.length === 0 || selectedPlaylistIds.length === 0)) {
            return;
        }
        if (isPlaylistQuickPublishPopupOpen && (selectedPlaylistIds.length === 0 || selectedScreenIds.length === 0)) {
            return;
        }

        // Time setting correct format
        let displayFromTime = null
        if (values.displayFromToggle === true && values.repeatYearly === false) {
            displayFromTime = values.displayFrom.toISOString()
        } else if (values.displayFromToggle === true && values.repeatYearly === true) {
            displayFromTime = values.displayFromAnnually.toISOString()
        }

        let displayUntilTime = null
        if (values.displayUntilToggle === true && values.repeatYearly === false) {
            displayUntilTime = values.displayUntil.toISOString()
        } else if (values.displayFromToggle === true && values.repeatYearly === true) {
            displayUntilTime = values.displayUntilAnnually.toISOString()
        }

        if (isTemplateQuickPublishPopupOpen) {
            await addNewPlaylistTemplateMultiple({
                templates: selectedTemplateIds,
                playlists: selectedPlaylistIds,
                data: {
                    duration: values.duration,
                    alwaysPlay: values.alwaysPlay,
                    displayFromToggle: values.displayFromToggle,
                    displayFrom: displayFromTime,
                    displayUntilToggle: values.displayUntilToggle,
                    displayUntil: displayUntilTime,
                    repeatYearly: values.repeatYearly,
                    displayOnToggle: values.displayOnToggle,
                    displayOn: {
                        monday: values.monday,
                        mondayStartFrom: (values.mondayStartFrom !== '' ? `${values.mondayStartFrom}:00` : null),
                        mondayEndAt: (values.mondayEndAt !== '' ? `${values.mondayEndAt}:00` : null),
                        tuesday: values.tuesday,
                        tuesdayStartFrom: (values.tuesdayStartFrom !== '' ? `${values.tuesdayStartFrom}:00` : null),
                        tuesdayEndAt: (values.tuesdayEndAt !== '' ? `${values.tuesdayEndAt}:00` : null),
                        wednesday: values.wednesday,
                        wednesdayStartFrom: (values.wednesdayStartFrom !== '' ? `${values.wednesdayStartFrom}:00` : null),
                        wednesdayEndAt: (values.wednesdayEndAt !== '' ? `${values.wednesdayEndAt}:00` : null),
                        thursday: values.thursday,
                        thursdayStartFrom: (values.thursdayStartFrom !== '' ? `${values.thursdayStartFrom}:00` : null),
                        thursdayEndAt: (values.thursdayEndAt !== '' ? `${values.thursdayEndAt}:00` : null),
                        friday: values.friday,
                        fridayStartFrom: (values.fridayStartFrom !== '' ? `${values.fridayStartFrom}:00` : null),
                        fridayEndAt: (values.fridayEndAt !== '' ? `${values.fridayEndAt}:00` : null),
                        saturday: values.saturday,
                        saturdayStartFrom: (values.saturdayStartFrom !== '' ? `${values.saturdayStartFrom}:00` : null),
                        saturdayEndAt: (values.saturdayEndAt !== '' ? `${values.saturdayEndAt}:00` : null),
                        sunday: values.sunday,
                        sundayStartFrom: (values.sundayStartFrom !== '' ? `${values.sundayStartFrom}:00` : null),
                        sundayEndAt: (values.sundayEndAt !== '' ? `${values.sundayEndAt}:00` : null),
                    },
                    startFrom: (values.startFrom !== '' ? `${values.startFrom}:00` : null),
                    endAt: (values.endAt !== '' ? `${values.endAt}:00` : null)
                },
            }).unwrap();
        } else if (isPlaylistQuickPublishPopupOpen) {
            await addNewScreenPlaylistMultiple({
                playlists: selectedPlaylistIds,
                screens: selectedScreenIds,
                data: {
                    alwaysPlay: values.alwaysPlay,
                    overrule: values.overrule,
                    displayFromToggle: values.displayFromToggle,
                    displayFrom: displayFromTime,
                    displayUntilToggle: values.displayUntilToggle,
                    displayUntil: displayUntilTime,
                    repeatYearly: values.repeatYearly,
                    displayOnToggle: values.displayOnToggle,
                    displayOn: {
                        monday: values.monday,
                        mondayStartFrom: (values.mondayStartFrom !== '' ? `${values.mondayStartFrom}:00` : null),
                        mondayEndAt: (values.mondayEndAt !== '' ? `${values.mondayEndAt}:00` : null),
                        tuesday: values.tuesday,
                        tuesdayStartFrom: (values.tuesdayStartFrom !== '' ? `${values.tuesdayStartFrom}:00` : null),
                        tuesdayEndAt: (values.tuesdayEndAt !== '' ? `${values.tuesdayEndAt}:00` : null),
                        wednesday: values.wednesday,
                        wednesdayStartFrom: (values.wednesdayStartFrom !== '' ? `${values.wednesdayStartFrom}:00` : null),
                        wednesdayEndAt: (values.wednesdayEndAt !== '' ? `${values.wednesdayEndAt}:00` : null),
                        thursday: values.thursday,
                        thursdayStartFrom: (values.thursdayStartFrom !== '' ? `${values.thursdayStartFrom}:00` : null),
                        thursdayEndAt: (values.thursdayEndAt !== '' ? `${values.thursdayEndAt}:00` : null),
                        friday: values.friday,
                        fridayStartFrom: (values.fridayStartFrom !== '' ? `${values.fridayStartFrom}:00` : null),
                        fridayEndAt: (values.fridayEndAt !== '' ? `${values.fridayEndAt}:00` : null),
                        saturday: values.saturday,
                        saturdayStartFrom: (values.saturdayStartFrom !== '' ? `${values.saturdayStartFrom}:00` : null),
                        saturdayEndAt: (values.saturdayEndAt !== '' ? `${values.saturdayEndAt}:00` : null),
                        sunday: values.sunday,
                        sundayStartFrom: (values.sundayStartFrom !== '' ? `${values.sundayStartFrom}:00` : null),
                        sundayEndAt: (values.sundayEndAt !== '' ? `${values.sundayEndAt}:00` : null),
                    },
                    startFrom: (values.startFrom !== '' ? `${values.startFrom}:00` : null),
                    endAt: (values.endAt !== '' ? `${values.endAt}:00` : null)
                },
            }).unwrap();
        }

        closeQuickPublish();
    }

    return (
        <Popup isOpen={isTemplateQuickPublishPopupOpen || isPlaylistQuickPublishPopupOpen} handleClose={closeQuickPublish} title={t('quick_publish.title')} containerId={'quick-publish-popup'} scrollable={true}>
            <div className="quick-publish-popup-container">
                <div className="row">

                    <div className="col-12 col-lg-6 col-xl-4 mb-3 mb-xl-0">
                        {isTemplateQuickPublishPopupOpen && <>
                            <CreationSelector creationType={'template'} selectedCreationIds={selectedTemplateIds} handleSelectedCreationsChange={handleSelectedCreationsChange} selectedAspectRatio={selectedAspectRatio} selectionError={templateSelectionError} />
                            <CreationOrder creationType={'template'} selectedCreationIds={selectedTemplateIds} setSelectedCreationIds={setSelectedTemplateIds} />
                        </>}
                        {isPlaylistQuickPublishPopupOpen && <>
                            <CreationSelector creationType={'playlist'} selectedCreationIds={selectedPlaylistIds} handleSelectedCreationsChange={handleSelectedCreationsChange} selectedAspectRatio={selectedAspectRatio} selectionError={playlistSelectionError} />
                            <CreationOrder creationType={'playlist'} selectedCreationIds={selectedPlaylistIds} setSelectedCreationIds={setSelectedPlaylistIds} />
                        </>}
                    </div>

                    <div className="col-12 col-lg-6 col-xl-4 mb-3 mb-xl-0">
                        {isTemplateQuickPublishPopupOpen && <>
                            <CreationSelector creationType={'playlist'} selectedCreationIds={selectedPlaylistIds} handleSelectedCreationsChange={handleSelectedCreationsChange} selectedAspectRatio={selectedAspectRatio} selectionError={playlistSelectionError} />
                        </>}
                        {isPlaylistQuickPublishPopupOpen && <>
                            <CreationSelector creationType={'screen'} selectedCreationIds={selectedScreenIds} handleSelectedCreationsChange={handleSelectedCreationsChange} selectedAspectRatio={selectedAspectRatio} selectionError={screenSelectionError} />
                        </>}
                    </div>

                    <div className="col-12 col-lg-12 col-xl-4 mb-3 mb-xl-0">
                        <ScheduleSettings onPublish={onPublish} />
                    </div>
                </div>

            </div>
        </Popup>
    );
}
